/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Component, EventEmitter, Input, Output, DOCUMENT, inject, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import {
  DYNAMIC_FORM_CONTROL_INPUT_TYPE_DATE,
  DYNAMIC_FORM_CONTROL_INPUT_TYPE_EMAIL,
  DYNAMIC_FORM_CONTROL_INPUT_TYPE_PASSWORD,
  DYNAMIC_FORM_CONTROL_INPUT_TYPE_TEL,
  DynamicInputModel,
} from '../../../model/input/dynamic-input.model'
import { DynamicFormControlLayout } from '../../../model/misc/dynamic-form-control-layout.model'
import { DynamicFormLayout, DynamicFormLayoutService } from '../../../service/dynamic-form-layout.service'
import { DynamicFormValidationService } from '../../../service/dynamic-form-validation.service'
import { BaseFormControlComponent } from '../base-form-control.component'

@Component({
  selector: 'dynamic-basic-input',
  templateUrl: './dynamic-basic-input.component.html',
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DynamicBasicInputComponent extends BaseFormControlComponent implements OnInit {
  private document = inject<Document>(DOCUMENT)
  // @ts-ignore
  layoutService: DynamicFormLayoutService
  // @ts-ignore
  validationService: DynamicFormValidationService
  protected readonly DYNAMIC_FORM_CONTROL_INPUT_TYPE_PASSWORD = DYNAMIC_FORM_CONTROL_INPUT_TYPE_PASSWORD
  protected readonly DYNAMIC_FORM_CONTROL_INPUT_TYPE_DATE = DYNAMIC_FORM_CONTROL_INPUT_TYPE_DATE
  protected readonly DYNAMIC_FORM_CONTROL_INPUT_TYPE_EMAIL = DYNAMIC_FORM_CONTROL_INPUT_TYPE_EMAIL
  protected readonly DYNAMIC_FORM_CONTROL_INPUT_TYPE_TEL = DYNAMIC_FORM_CONTROL_INPUT_TYPE_TEL
  // @ts-ignore
  @Input() formLayout: DynamicFormLayout
  // @ts-ignore
  @Input() group: UntypedFormGroup
  // @ts-ignore
  @Input() layout: DynamicFormControlLayout

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() blur: EventEmitter<any> = new EventEmitter()
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() change: EventEmitter<any> = new EventEmitter()
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() focus: EventEmitter<any> = new EventEmitter()

  public shouldAddPlaceholderNotShownClass = false

  constructor() {
    const layoutService = inject(DynamicFormLayoutService)
    const validationService = inject(DynamicFormValidationService)

    super()

    this.layoutService = layoutService
    this.validationService = validationService
  }

  private _model!: DynamicInputModel

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  get model(): DynamicInputModel {
    return this._model
  }

  @Input() set model(newModel: DynamicInputModel) {
    this._model = newModel
    this.shouldAddPlaceholderNotShownClass =
      !!this._model.placeholder && this.document.documentElement.classList.contains('no-placeholder-support')
  }

  get descriptionText(): string {
    return this.model?.description || (this.model as DynamicInputModel)?.hint || ''
  }

  public ngOnInit(): void {
    // Ensure the model value is never null or undefined
    if (this._model && (this._model.value === null || this._model.value === undefined)) {
      this._model.value = ''
    }
  }
}
